class BookingsOverview extends HTMLElement {
  async connectedCallback() {
    this.settings = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? this.dataset.timezone,
      maxMonthsFuture: 12,
      maxMonthsPast: 0,
      perPage: parseInt(this.dataset.perPage, 10) || 6,
      storefrontToken: this.dataset.storefrontToken,
      storeDomain: this.dataset.storeDomain,
      translations: JSON.parse(this.querySelector('[data-bookings-translations]').textContent)
    };

    this.templateTile = this.dayTiles[0].cloneNode(true);
    this.templateItem = this.querySelector('[data-booking-item-template]').content.firstElementChild;
    this.today = this.getNowInTimezone();
    this.currentMonthDate = new Date(this.today.year, this.today.month, 1);
    this.selectedDate = null;
    this.currentPage = 1;

    this.renderWeekdays();

    this.grid.addEventListener('click', this.handleSelectGridDay.bind(this));
    this.prevButton?.addEventListener('click', this.handleClickPrevMonth.bind(this));
    this.nextButton?.addEventListener('click', this.handleClickNextMonth.bind(this));
    this.viewAllButton?.addEventListener('click', this.handleViewAll.bind(this));
    this.prevPageButton?.addEventListener('click', this.handleClickPrevPage.bind(this));
    this.nextPageButton?.addEventListener('click', this.handleClickNextPage.bind(this));

    this.bookings = await this.fetchUpcomingBookings();
    this.renderCalendar();
    this.renderList();
  }

  // — Getters: DOM —

  get grid() {
    return this.querySelector('[data-events-calendar-grid]');
  }

  get monthLabel() {
    return this.querySelector('[data-events-calendar-month]');
  }

  get prevButton() {
    return this.querySelector('[data-events-calendar-prev]');
  }

  get nextButton() {
    return this.querySelector('[data-events-calendar-next]');
  }

  get weekdayElements() {
    return [...this.querySelectorAll('[data-events-calendar-weekday]')];
  }

  get dayTiles() {
    return [...this.querySelectorAll('[data-events-calendar-tile]')];
  }

  get listHeading() {
    return this.querySelector('[data-bookings-list-heading]');
  }

  get viewAllButton() {
    return this.querySelector('[data-bookings-view-all]');
  }

  get listContainer() {
    return this.querySelector('[data-bookings-list]');
  }

  get emptyState() {
    return this.querySelector('[data-bookings-empty]');
  }

  get paginationContainer() {
    return this.querySelector('[data-bookings-pagination]');
  }

  get pageStatus() {
    return this.querySelector('[data-bookings-page-status]');
  }

  get prevPageButton() {
    return this.querySelector('[data-bookings-prev-page]');
  }

  get nextPageButton() {
    return this.querySelector('[data-bookings-next-page]');
  }

  // — Getters: State —

  get bookingsForSelectedDate() {
    if (!this.selectedDate) return this.bookings;
    return this.bookings.filter(booking => booking.date_filter === this.selectedDate);
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.bookingsForSelectedDate.length / this.settings.perPage));
  }

  // — Event Handlers —

  handleSelectGridDay(e) {
    e.preventDefault();
    const tile = e.target.closest('[data-events-calendar-tile]');
    if (!tile || tile.dataset.state !== 'filled') return;

    const dateKey = tile.dataset.eventsCalendarDate;
    this.selectedDate = this.selectedDate === dateKey ? null : dateKey;
    this.currentPage = 1;
    this.updateSelectedTile();
    this.renderList();
  }

  handleClickNextMonth(e) {
    e.preventDefault();
    this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
    this.renderCalendar();
  }

  handleClickPrevMonth(e) {
    e.preventDefault();
    this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
    this.renderCalendar();
  }

  handleViewAll(e) {
    e.preventDefault();
    this.selectedDate = null;
    this.currentPage = 1;
    this.updateSelectedTile();
    this.renderList();
  }

  handleClickPrevPage(e) {
    e.preventDefault();
    if (this.currentPage <= 1) return;
    this.currentPage -= 1;
    this.renderList();
  }

  handleClickNextPage(e) {
    e.preventDefault();
    if (this.currentPage >= this.totalPages) return;
    this.currentPage += 1;
    this.renderList();
  }

  // — Render —

  renderWeekdays() {
    this.weekdayElements.forEach(weekday => {
      const translation = this.settings.translations[`${weekday.dataset.eventsCalendarWeekday}_short`];
      if (translation) weekday.textContent = translation;
    });
  }

  renderCalendar() {
    const year = this.currentMonthDate.getFullYear();
    const month = this.currentMonthDate.getMonth();

    this.monthLabel.textContent = `${this.settings.translations[this.getMonthName(month)]} ${year}`;
    this.grid.innerHTML = '';

    const firstDay = new Date(year, month, 1);
    const weekdayOrder = this.getWeekdayOrder();
    const startDay = weekdayOrder.indexOf(this.getWeekdayName(firstDay.getDay()));
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayKey = this.formatDateKey(new Date(this.today.year, this.today.month, this.today.day));

    this.updateButtonStates();

    for (let i = 0; i < 42; i++) {
      const tile = this.templateTile.cloneNode(true);
      const dayElement = tile.querySelector('[data-events-calendar-tile-day]');
      const dayNumber = i - startDay + 1;

      tile.removeAttribute('data-events-calendar-date');

      if (dayNumber < 1 || dayNumber > daysInMonth) {
        tile.setAttribute('data-state', 'hidden');
        dayElement.textContent = '';
        this.grid.appendChild(tile);
        continue;
      }

      const currentDate = new Date(year, month, dayNumber);
      const dateKey = this.formatDateKey(currentDate);
      const isPast = dateKey < todayKey;
      const hasBookings = this.bookings.some(booking => booking.date_filter === dateKey);

      tile.setAttribute('data-state', !isPast && hasBookings ? 'filled' : 'empty');
      tile.setAttribute('data-events-calendar-date', dateKey);
      dayElement.textContent = dayNumber;

      if (hasBookings) tile.setAttribute('data-has-bookings', 'true');
      if (dateKey === todayKey) tile.setAttribute('data-today', 'true');
      if (this.selectedDate === dateKey) tile.setAttribute('data-selected', 'true');

      this.grid.appendChild(tile);
    }
  }

  renderList() {
    const bookings = this.bookingsForSelectedDate;
    const start = (this.currentPage - 1) * this.settings.perPage;
    const pageBookings = bookings.slice(start, start + this.settings.perPage);

    this.listHeading.textContent = this.selectedDate
      ? `${this.settings.translations.bookings_for} ${this.formatDateLabel(this.selectedDate)}`
      : this.settings.translations.upcoming_bookings;

    this.viewAllButton.classList.toggle('hidden', !this.selectedDate);

    this.listContainer.innerHTML = '';
    pageBookings.forEach(booking => {
      const item = this.templateItem.cloneNode(true);
      item.querySelector('[data-booking-service]').textContent = this.getServiceLabel(booking);
      item.querySelector('[data-booking-date]').textContent = this.getBookingDateTimeLabel(booking);
      item.querySelector('[data-booking-customer]').textContent = this.getCustomerLabel(booking);
      this.listContainer.appendChild(item);
    });

    this.emptyState.classList.toggle('hidden', bookings.length > 0);
    this.paginationContainer.classList.toggle('hidden', bookings.length <= this.settings.perPage);
    this.pageStatus.textContent = this.settings.translations.pagination_status
      .replace('{{ current }}', this.currentPage)
      .replace('{{ total }}', this.totalPages);
    this.prevPageButton.disabled = this.currentPage <= 1;
    this.nextPageButton.disabled = this.currentPage >= this.totalPages;
  }

  updateSelectedTile() {
    this.querySelector('[data-events-calendar-tile][data-selected]')?.removeAttribute('data-selected');
    if (this.selectedDate) {
      this.querySelector(`[data-events-calendar-tile][data-events-calendar-date="${this.selectedDate}"]`)?.setAttribute('data-selected', 'true');
    }
  }

  // — Data —

  async fetchUpcomingBookings() {
    const query = `{
      metaobjects(type: "booking_holds", first: 250) {
        edges {
          node {
            fields {
              key
              value
              reference {
                ... on Product { title }
                ... on ProductVariant { title }
              }
            }
          }
        }
      }
    }`;

    const response = await fetch(`https://${this.settings.storeDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.settings.storefrontToken
      },
      body: JSON.stringify({ query })
    });

    const { data } = await response.json();

    const todayKey = this.formatDateKey(new Date(this.today.year, this.today.month, this.today.day));

    return data.metaobjects.edges
      .map(({ node }) => {
        const fields = {};
        node.fields.forEach(({ key, value, reference }) => {
          fields[key] = reference ? reference.title : value;
        });
        return fields;
      })
      .filter(booking => booking.archived !== 'true' && booking.date_filter >= todayKey)
      .sort((a, b) => a.when.localeCompare(b.when));
  }

  // — Utilities —

  getServiceLabel(booking) {
    if (booking.variant && booking.variant !== 'Default Title' && booking.variant !== booking.product) {
      return `${booking.product} - ${booking.variant}`;
    }
    return booking.product;
  }

  getCustomerLabel(booking) {
    return booking.customer_name || booking.customer_email || this.settings.translations.guest;
  }

  getBookingDateTimeLabel(booking) {
    const time = booking.when.substring(11, 16);
    const [hour, minute] = time.split(':').map(Number);
    const period = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${this.formatDateLabel(booking.date_filter)} at ${hour12}:${String(minute).padStart(2, '0')}${period}`;
  }

  updateButtonStates() {
    const prevDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
    const nextDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
    this.setButtonState(this.prevButton, this.canGoToMonth(prevDate));
    this.setButtonState(this.nextButton, this.canGoToMonth(nextDate));
  }

  setButtonState(button, isActive) {
    if (!button) return;
    button.disabled = !isActive;
    button.setAttribute('aria-disabled', isActive ? 'false' : 'true');
  }

  canGoToMonth(date) {
    const minDate = new Date(this.today.year, this.today.month - this.settings.maxMonthsPast, 1);
    const maxDate = new Date(this.today.year, this.today.month + this.settings.maxMonthsFuture, 1);
    return date >= minDate && date <= maxDate;
  }

  getMonthName(monthIndex) {
    return ['january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'][monthIndex];
  }

  getWeekdayName(dayIndex) {
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayIndex];
  }

  getWeekdayOrder() {
    return this.weekdayElements.map(weekday => weekday.dataset.eventsCalendarWeekday);
  }

  getNowInTimezone() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.settings.timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).formatToParts(new Date());

    const values = {};
    parts.forEach(part => values[part.type] = part.value);

    return {
      year: Number(values.year),
      month: Number(values.month) - 1,
      day: Number(values.day)
    };
  }

  formatDateKey(date) {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }

  formatDateLabel(dateKey) {
    const date = new Date(dateKey + 'T00:00:00');
    const day = date.getDate();
    const ordinal = n => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return [
      this.settings.translations[this.getWeekdayName(date.getDay())] + ',',
      ordinal(day),
      this.settings.translations[this.getMonthName(date.getMonth())],
      date.getFullYear()
    ].join(' ');
  }
}

customElements.define('bookings-overview', BookingsOverview);
